import { useRef, useState } from "react";
import { pdfToImages, extractTextFromFile } from "../../supabase";
import { callClaudeWithMessages } from "../../services/claude";
import { inputCls, Field } from "../common/FormHelpers";

// Drop the LOI we sent; it becomes an Outreach deal.
//
// The LOI is read the way the OM upload reads an OM (page images for a PDF,
// text for Word): addresses, total SF, the price we offered, the LOI date and
// the key terms. One LOI is one deal - several addresses make a portfolio.
// The brokerage and broker it went through are typed here, since an LOI
// rarely names the broker it was sent through. Everything is editable before
// saving; nothing is saved until "Add to Outreach".

const PROMPT = `You are reading a Letter of Intent (LOI) that a commercial real estate buyer (ProximityParks USA) sent to purchase industrial property.
Return ONLY a JSON object, no prose, with these keys:
{
  "propertyName": string,        // the property / portfolio name if the LOI gives one, else ""
  "addresses": [string],          // every property address on the LOI, full street, city, state, zip where given
  "squareFeet": number,           // total rentable SF across all properties, 0 if not stated
  "offerPrice": number,           // the total purchase price offered, in dollars, 0 if not stated
  "loiDate": "YYYY-MM-DD",        // the date of the letter, "" if not stated
  "deposit": number,              // earnest money / deposit in dollars (total of all deposits), 0 if not stated
  "ddDays": number,               // due diligence / inspection period in days, 0 if not stated
  "closingDays": number,          // days to close after the due diligence period (or after PSA), 0 if not stated
  "terms": string,                // 2-5 short lines on any other key terms: financing contingency, closing costs, assignment, expiration of the offer, seller financing, etc.
  "brokerFirm": string,           // ONLY if the LOI itself names the brokerage it is addressed to / delivered through, else ""
  "brokerName": string            // ONLY if the LOI names the broker, else ""
}
Rules: addresses is always an array. Numbers are plain numbers (no $ or commas). Do not invent values - use "" or 0 when the LOI does not say.`;

const todayPlus = (days) => {
  const d = new Date();
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const EMPTY = {
  propertyName: "", addresses: [""], squareFeet: "", offerPrice: "", loiDate: new Date().toISOString().slice(0, 10),
  deposit: "", ddDays: "", closingDays: "", terms: "",
  brokerFirm: "", brokerName: "", brokerEmail: "", brokerPhone: "", followUpDate: todayPlus(7),
};

export default function AddDealFromLOI({ onSave, onCancel, onOpenExisting }) {
  const [step, setStep] = useState("upload");            // upload | extracting | review
  const [file, setFile] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  async function read(f) {
    setFile(f);
    setStep("extracting");
    setError(null);
    try {
      let messages;
      if (f.type === "application/pdf" || f.name.toLowerCase().endsWith(".pdf")) {
        const { images } = await pdfToImages(f, 8);
        const content = images.map((data) => ({ type: "image", source: { type: "base64", media_type: "image/jpeg", data } }));
        content.push({ type: "text", text: "Extract the LOI details." });
        messages = [{ role: "user", content }];
      } else {
        const text = await extractTextFromFile(f);
        if (!text || text.trim().length < 50) throw new Error("Couldn't read enough text from this file - is it a scanned image? Try the PDF.");
        messages = [{ role: "user", content: text.slice(0, 40000) }];
      }
      const reply = await callClaudeWithMessages(PROMPT, messages, 1500);
      const x = JSON.parse(reply.replace(/```json?\s*/gi, "").replace(/```/g, "").trim());
      const num = (v) => (v && Number(v) ? String(v) : "");
      const addresses = Array.isArray(x.addresses) && x.addresses.length ? x.addresses.map(String) : [""];
      setForm((cur) => ({
        ...cur,
        propertyName: x.propertyName || "",
        addresses,
        squareFeet: num(x.squareFeet),
        offerPrice: num(x.offerPrice),
        loiDate: /^\d{4}-\d{2}-\d{2}$/.test(x.loiDate || "") ? x.loiDate : cur.loiDate,
        deposit: num(x.deposit),
        ddDays: num(x.ddDays),
        closingDays: num(x.closingDays),
        terms: x.terms || "",
        brokerFirm: x.brokerFirm || cur.brokerFirm,
        brokerName: x.brokerName || cur.brokerName,
      }));
      setStep("review");
    } catch (e) {
      setError(`Couldn't read the LOI: ${e.message}. You can enter it by hand.`);
      setStep("review");
    }
  }

  async function save(allowDuplicate = false) {
    setSaveError(null);
    const addresses = form.addresses.map((a) => a.trim()).filter(Boolean);
    if (!addresses.length) return setSaveError("At least one address is required.");
    if (!Number(form.offerPrice)) return setSaveError("The offer price is required.");
    if (!form.brokerFirm.trim() || !form.brokerName.trim()) return setSaveError("Brokerage and broker name are required.");
    const name = form.propertyName.trim() || addresses[0];
    const n = (v) => (v === "" || v == null || Number.isNaN(Number(v)) ? null : Number(v));
    const notes = [`Name: ${name}`, file ? `[LOI: ${file.name}]` : "[LOI entered by hand]", form.terms.trim()].filter(Boolean).join("\n");
    setSaving(true);
    let failure = null;
    await onSave({
      dealFlow: "outreach",
      stage: "LOI Sent",
      addresses,
      market: "",
      squareFeet: n(form.squareFeet) || 0,
      askingPrice: 0,                       // the seller's ask is unknown; our number is offerPrice
      capRate: 0,
      sourceNotes: notes,
      offerPrice: n(form.offerPrice),
      loiDate: form.loiDate || null,
      loiDeposit: n(form.deposit),
      loiDdDays: n(form.ddDays),
      loiClosingDays: n(form.closingDays),
      loiTerms: form.terms.trim() || null,
      brokerFirm: form.brokerFirm.trim(),
      brokerName: form.brokerName.trim(),
      brokerEmail: form.brokerEmail.trim() || null,
      brokerPhone: form.brokerPhone.trim() || null,
      followUpDate: form.followUpDate || null,
    }, (e) => { failure = e; setSaveError(e); }, file ? [file] : [], null, { allowDuplicate });
    setSaving(false);
    return failure;
  }

  const shell = (children) => (
    <main className="max-w-3xl mx-auto px-6 py-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-lg font-semibold text-[#f4f4f4]">Upload an LOI</h2>
          <p className="text-xs text-[#8a8a8a] mt-0.5">The LOI we sent through a broker. It lands in Outreach as LOI Sent.</p>
        </div>
        <button onClick={onCancel} className="text-sm text-[#8a8a8a] hover:text-[#f4f4f4]">Cancel</button>
      </div>
      {children}
    </main>
  );

  if (step === "upload") {
    return shell(
      <>
        <div
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); if (e.dataTransfer.files?.[0]) read(e.dataTransfer.files[0]); }}
          className={`border-2 border-dashed rounded-xl p-12 text-center cursor-pointer transition-colors ${dragging ? "border-[#e93529] bg-[rgba(233,53,41,0.05)]" : "border-[#4a4a4a] hover:border-[#e93529]"}`}>
          <p className="text-4xl mb-3">📄</p>
          <p className="text-sm text-[#f4f4f4] font-medium">Drop the LOI here, or click to choose it</p>
          <p className="text-xs text-[#8a8a8a] mt-1">PDF or Word. We read the addresses, SF, offer price, date and key terms.</p>
          <input ref={inputRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => e.target.files?.[0] && read(e.target.files[0])} />
        </div>
        <button onClick={() => setStep("review")} className="mt-4 text-xs text-[#8a8a8a] hover:text-[#f4f4f4] underline">Enter an LOI by hand instead</button>
      </>
    );
  }
  if (step === "extracting") {
    return shell(<div className="text-center py-24 text-[#8a8a8a] text-sm">Reading {file?.name}…</div>);
  }

  const dup = saveError && typeof saveError === "object" && saveError.code === "duplicate_deal" ? saveError : null;
  return shell(
    <div className="space-y-5">
      {error && <p className="text-xs text-[#c8922f] bg-[rgba(200,146,47,0.1)] border border-[rgba(200,146,47,0.3)] rounded-lg px-3 py-2">{error}</p>}
      {file && <p className="text-xs text-[#8a8a8a]">From <span className="text-[#f4f4f4]">{file.name}</span> - check every field; the file is attached to the deal on save.</p>}

      <div className="bg-[#242424] border border-[#3a3a3a] rounded-xl p-5 space-y-4">
        <h3 className="text-xs font-bold uppercase tracking-widest text-[#8a8a8a]">The LOI</h3>
        <Field label="Deal name"><input className={inputCls()} value={form.propertyName} onChange={(e) => set("propertyName", e.target.value)} placeholder="Defaults to the first address" /></Field>
        <Field label="Addresses" required>
          <div className="space-y-2">
            {form.addresses.map((a, i) => (
              <div key={i} className="flex gap-2">
                <input className={inputCls()} value={a} placeholder="Street, city, state zip"
                  onChange={(e) => set("addresses", form.addresses.map((x, j) => (j === i ? e.target.value : x)))} />
                {form.addresses.length > 1 && (
                  <button onClick={() => set("addresses", form.addresses.filter((_, j) => j !== i))} className="text-[#8a8a8a] hover:text-[#f04f44] px-2">✕</button>
                )}
              </div>
            ))}
            <button onClick={() => set("addresses", [...form.addresses, ""])} className="text-xs text-[#f04f44] hover:text-[#e93529]">+ Add address</button>
          </div>
        </Field>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Total SF"><input type="number" className={inputCls()} value={form.squareFeet} onChange={(e) => set("squareFeet", e.target.value)} /></Field>
          <Field label="Our offer ($)" required><input type="number" className={inputCls()} value={form.offerPrice} onChange={(e) => set("offerPrice", e.target.value)} /></Field>
          <Field label="LOI date"><input type="date" className={inputCls()} value={form.loiDate} onChange={(e) => set("loiDate", e.target.value)} /></Field>
        </div>
        {Number(form.offerPrice) > 0 && Number(form.squareFeet) > 0 && (
          <p className="text-xs text-[#8a8a8a] -mt-2">${Math.round(Number(form.offerPrice) / Number(form.squareFeet))}/SF</p>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Field label="Deposit ($)"><input type="number" className={inputCls()} value={form.deposit} onChange={(e) => set("deposit", e.target.value)} /></Field>
          <Field label="Due diligence (days)"><input type="number" className={inputCls()} value={form.ddDays} onChange={(e) => set("ddDays", e.target.value)} /></Field>
          <Field label="Closing (days)"><input type="number" className={inputCls()} value={form.closingDays} onChange={(e) => set("closingDays", e.target.value)} /></Field>
        </div>
        <Field label="Other key terms"><textarea rows={3} className={inputCls()} value={form.terms} onChange={(e) => set("terms", e.target.value)} /></Field>
      </div>

      <div className="bg-[#242424] border border-[#3a3a3a] rounded-xl p-5 space-y-4">
        <h3 className="text-xs font-bold uppercase tracking-widest text-[#8a8a8a]">Submitted through</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Brokerage" required><input className={inputCls()} value={form.brokerFirm} onChange={(e) => set("brokerFirm", e.target.value)} placeholder="e.g. CBRE" /></Field>
          <Field label="Broker name" required><input className={inputCls()} value={form.brokerName} onChange={(e) => set("brokerName", e.target.value)} /></Field>
          <Field label="Broker email"><input type="email" className={inputCls()} value={form.brokerEmail} onChange={(e) => set("brokerEmail", e.target.value)} /></Field>
          <Field label="Broker phone"><input className={inputCls()} value={form.brokerPhone} onChange={(e) => set("brokerPhone", e.target.value)} /></Field>
        </div>
        <Field label="Follow up on"><input type="date" className={inputCls()} value={form.followUpDate} onChange={(e) => set("followUpDate", e.target.value)} /></Field>
      </div>

      {saveError && !dup && <p className="text-xs text-[#f04f44]">{String(saveError.message || saveError)}</p>}
      {dup && (
        <div className="text-xs bg-[rgba(200,146,47,0.1)] border border-[rgba(200,146,47,0.3)] rounded-lg px-3 py-2 space-y-2">
          <p className="text-[#c8922f]">Proxy already has a deal at this address:</p>
          {dup.duplicates.map(({ deal: d, reason }) => (
            <div key={d.id} className="flex items-center justify-between gap-3">
              <span className="text-[#f4f4f4]">{(d.source_notes || "").match(/^Name:\s*(.+)$/m)?.[1] || "Deal"} · {d.deal_stage}{reason ? ` · ${reason}` : ""}</span>
              <button onClick={() => onOpenExisting?.(d.id)} className="text-[#f04f44] underline">Open it</button>
            </div>
          ))}
          <button onClick={() => save(true)} className="text-[#8a8a8a] underline">Add the LOI as a separate Outreach deal anyway</button>
        </div>
      )}

      <div className="flex justify-end gap-3">
        <button onClick={onCancel} className="text-sm text-[#8a8a8a] hover:text-[#f4f4f4] px-4 py-2">Cancel</button>
        <button onClick={() => save(false)} disabled={saving}
          className="bg-pp-red hover:bg-pp-red-light disabled:opacity-50 text-white font-semibold text-sm px-5 py-2 rounded-lg" style={{ color: "#ffffff" }}>
          {saving ? "Saving…" : "Add to Outreach"}
        </button>
      </div>
    </div>
  );
}
